import React, { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { QRCodeCanvas } from "qrcode.react";
import PGService from "../../services/PGService";

Modal.setAppElement("#root");

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    padding: "30px",
    maxWidth: "520px",
    width: "100%",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
};

const AddMoneyModal = ({
  isOpen,
  onRequestClose,
  balanceData,
  fetchBalance,
}) => {
  const [amount, setAmount] = useState("");
  const [orderId, setOrderId] = useState("");
  const [upiUrl, setUpiUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);
  const [activeTab, setActiveTab] = useState("UPI"); // 👈 NEW STATE for tabs
  const timerRef = useRef(null);
  const intervalRef = useRef(null);

  const handleAddMoney = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      return Swal.fire("Oops!", "Please enter a valid amount", "error");
    }

    setLoading(true);
    try {
      const result = await PGService.fpayCreateOrder(amount);

      if (!result.success) {
        return Swal.fire("Oops!", result.message || "Failed to fetch", "error");
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;

        if (data.upi_intent != null) {
          setUpiUrl(data.upi_intent);
          setOrderId(data.order_id);
          setTimeLeft(180);

          timerRef.current = setInterval(() => {
            setTimeLeft((prev) => {
              if (prev <= 1) {
                clearInterval(timerRef.current);
                clearInterval(intervalRef.current);
                setUpiUrl("");
                setOrderId("");
                Swal.fire(
                  "QR Expired",
                  "Please try again to get a new QR code",
                  "info",
                );
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          if (data.redirect_url) {
            window.location.href = data.redirect_url;
          }
        }
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || "Request failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const txnStatus = async () => {
    if (!orderId) return;
    try {
      const result = await PGService.txnStatus(orderId);
      const responseData = result.data;

      if (result.success && responseData.status === "SUCCESS") {
        await fetchBalance();
        Swal.fire(
          "Success!",
          responseData.message || "Money added successfully",
          "success",
        );
        closeHandler();
      } else if (responseData.message === "FAILED") {
        Swal.fire(
          "Transaction Failed!",
          responseData?.data?.remark || "Transaction failed at the bank end",
          "error",
        );
        closeHandler();
      }
    } catch (error) {
      console.error("Transaction status check failed:", error);
    }
  };

  // Poll for txn status when UPI QR is active
  useEffect(() => {
    if (upiUrl && orderId) {
      intervalRef.current = setInterval(() => {
        txnStatus();
      }, 15000);
    }
    return () => clearInterval(intervalRef.current);
  }, [upiUrl, orderId]);

  const closeHandler = () => {
    setAmount("");
    setUpiUrl("");
    setOrderId("");
    setTimeLeft(180);
    setActiveTab("UPI");
    clearInterval(timerRef.current);
    clearInterval(intervalRef.current);
    onRequestClose();
  };

  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    if (!isOpen) {
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    }
    return () => {
      clearInterval(timerRef.current);
      clearInterval(intervalRef.current);
    };
  }, [isOpen]);

  const [vpa, setVpa] = useState("");

  const handleCollectRequest = async () => {
    if (!vpa || !amount) {
      return Swal.fire("Oops!", "Please enter VPA and amount", "warning");
    }

    setLoading(true);
    try {
      const result = await PGService.fpCollectRequest(vpa, amount);
      const responseData = result.data;

      if (result.success && responseData.status === "SUCCESS") {
        Swal.fire(
          "Success!",
          responseData.message || "Collect request sent successfully",
          "success",
        );
      } else {
        Swal.fire(
          "Opps!",
          responseData.message || "Collect request failed",
          "error",
        );
      }
    } catch (error) {
      Swal.fire(
        "Error!",
        error.response?.data?.message || "Request failed",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeHandler}
      style={customStyles}
      contentLabel="Add Money"
    >
      <div>
        <h4 className="mb-3 text-center text-primary fw-bold">
          Add Money to Wallet
        </h4>

        {/* 💠 Wallet Card */}
        <div className="mb-4 d-flex gap-3">
          <div className="card flex-fill text-center shadow-sm border-0">
            <div className="card-body">
              <i className="bi bi-wallet2 fs-3 text-primary mb-2"></i>
              <h6 className="card-title mb-1">Wallet Balance</h6>
              <p className="card-text fw-bold" style={{ fontSize: "18px" }}>
                ₹{balanceData?.main_wallet || "0.00"}
              </p>
            </div>
          </div>
        </div>

        {/* 🔹 TAB HEADERS */}
        <div className="mb-3 d-flex justify-content-center border-bottom pb-2">
          <button
            className={`btn me-2 ${activeTab === "UPI" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setActiveTab("UPI")}
          >
            UPI
          </button>
          {/* <button
            className={`btn ${
              activeTab === "COLLECT" ? "btn-primary" : "btn-outline-primary"
            }`}
            onClick={() => setActiveTab("COLLECT")}
          >
            UPI Collect
          </button> */}
        </div>

        {/* 🔸 TAB CONTENTS */}
        {activeTab === "UPI" && (
          <>
            {!upiUrl ? (
              <>
                <div className="mb-3">
                  <label className="form-label">Enter Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. 500"
                  />
                </div>

                <button
                  className="btn btn-success w-100"
                  onClick={handleAddMoney}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Generate QR to Pay"}
                </button>
              </>
            ) : (
              <div className="text-center">
                <h6 className="fw-semibold mb-3">
                  Scan this QR code to Add Money
                </h6>
                <QRCodeCanvas value={upiUrl} size={200} />
                <p className="mt-2 text-danger fw-bold">
                  QR expires in: {formatTime(timeLeft)}
                </p>
                <p className="text-muted small">
                  Scan & Pay with any BHIM UPI App
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === "COLLECT" && (
          <div className="mt-3">
            <h6 className="fw-semibold mb-3 text-center text-primary">
              UPI Collect Request
            </h6>

            <div className="mb-3">
              <label className="form-label">VPA Address</label>
              <input
                type="text"
                className="form-control"
                value={vpa}
                onChange={(e) => setVpa(e.target.value)}
                placeholder="Enter VPA Address (e.g. yourname@upi)"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Amount</label>
              <input
                type="number"
                className="form-control"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter Amount"
              />
            </div>

            <button
              className="btn btn-success w-100"
              onClick={handleCollectRequest}
              disabled={loading}
            >
              {loading ? "Sending Request..." : "Send Collect Request"}
            </button>

            <div className="mt-3">
              <p className="text-muted small mb-1">
                ⚠️{" "}
                <strong>
                  Payment allowed only to registered name UPI VPA address.
                </strong>
              </p>
              <p className="text-muted small">
                💰 <strong>Zero charges</strong> to add money using bank-linked
                UPI.
              </p>
            </div>
          </div>
        )}

        <div className="text-end mt-4">
          <button className="btn btn-outline-secondary" onClick={closeHandler}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddMoneyModal;
